/**
 * ProspectPanel - Panel lateral de prospecto unificado
 * Soporta dos modos:
 *   mode="chat"     -> Vista de Chats (incluye info técnica, horarios preferidos, botones rojos)
 *   mode="todolist"  -> Vista TodoList (incluye estado de venta, callback de notas)
 *
 * Props:
 *   contact   - { phone, name, id, assigned_to?, assignedTo?, deviceSource?, deviceInfoUpdatedAt? }
 *   onClose   - Función para cerrar/colapsar el panel
 *   onUpdate  - (phone, changes) => void  — callback cuando cambian datos del contacto
 *   mode      - 'chat' | 'todolist' (default: 'todolist')
 */

import { useState, useEffect, useCallback } from 'react'
import { useProspect } from '../../hooks/useProspect'
import { iconsAPI, notesAPI, prospectAPI } from '../../services/api'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import ContactAvatar from '../shared/ContactAvatar'
import AvatarPickerModal from './AvatarPickerModal'
import { getDateUrgency } from '../../utils/dateUtils'
import { formatPhoneCL } from '../../utils/phoneUtils'
import AccordionSection from './AccordionSection'
import TicketsSection from './TicketsSection'

function ProspectCampaignsSection({ contactPhone }) {
  const [enrollments, setEnrollments] = useState([])
  useEffect(() => {
    if (!contactPhone) return
    // [F2] Sesion por cookie httpOnly (same-origin): sin header Authorization
    fetch(`/api/crm-campaigns/contact/${encodeURIComponent(contactPhone)}/enrollments`)
      .then(r => r.json()).then(d => setEnrollments(d.enrollments || []))
  }, [contactPhone])

  if (enrollments.length === 0) return <div className="text-sm text-gray-400">Sin campañas</div>

  const statusLabel = (e) => {
    if (e.status === 'active') return `En curso paso ${e.current_step}`
    if (e.status === 'completed') return 'Completada'
    if (e.status === 'exited') return e.exit_reason === 'replied' ? 'Salio por respuesta' : (e.exit_reason === 'cohort_cancelled' ? 'Cancelada' : 'Salida')
    return e.status
  }

  return (
    <div className="space-y-2">
      {enrollments.map(e => (
        <div key={e.id} className="p-2 bg-gray-50 dark:bg-surface-dark-elev rounded-lg text-sm">
          <div className="font-medium">{e.campaign_name}</div>
          <div className="text-xs text-gray-500">
            {new Date(e.enrolled_at).toLocaleDateString('es-CL')} - {statusLabel(e)}
          </div>
        </div>
      ))}
    </div>
  )
}

import FormField from './FormField'
import NotesSection from '../todolist/NotesSection'
import CallToggle from '../calls/CallToggle'
import AgentSelector from '../shared/AgentSelector'
import CallHistorySection from '../calls/CallHistorySection'
import SupervisorAnalysis from './SupervisorAnalysis'
import IconSelectorModal from '../shared/IconSelectorModal'
import RatingIcon, { NOMBRE_MAP } from '../shared/RatingIcon'
import { Tooltip } from '../shared/Tooltip'
import { ICON_DESCRIPTIONS } from '../../utils/iconDescriptions'
import { colorForAgent } from '../../utils/agentColors'

export default function ProspectPanel({ contact, onClose, onUpdate, onDraftChanged, mode = 'todolist', onIcPendingEnqueue }) {
  const isChat = mode === 'chat'

  // Callback cuando auto-guardado o manual completa (solo relevante para todolist)
  const handleSaveComplete = (savedPhone, changes) => {
    if (onUpdate) onUpdate(savedPhone, changes)
  }

  const prospectOptions = isChat ? {} : { onSaveComplete: handleSaveComplete }
  const { prospect, loading, error, saving, updateField, saveNow, refresh } = useProspect(
    contact?.phone,
    prospectOptions
  )

  // Callback cuando NotesSection aplica acciones (todolist: recargar y notificar padre)
  const handleNotesUpdate = async (extraChanges = {}) => {
    if (!isChat) {
      await refresh()
      if (onUpdate && contact?.phone) {
        onUpdate(contact.phone, { _forceRefresh: true, ...extraChanges })
      }
    }
  }

  // ESC: guardar (defensivo) y cerrar el panel. Funciona aunque haya un input con foco.
  useEffect(() => {
    if (isChat) return // en modo chat el panel se gestiona distinto, no aplica
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return
      // No interferir si hay un modal/dropdown listening (sus propios stopPropagation lo manejan)
      e.preventDefault()
      const finishClose = () => { if (onClose) onClose() }
      if (typeof saveNow === 'function') {
        Promise.resolve(saveNow()).catch(() => {}).finally(finishClose)
      } else {
        finishClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isChat, saveNow, onClose])

  // Estados para iconos
  const [availableIcons, setAvailableIcons] = useState([])
  const [selectedIcon, setSelectedIcon] = useState(null)
  const [iconHistory, setIconHistory] = useState([])
  const [loadingIcons, setLoadingIcons] = useState(false)
  const [showIconModal, setShowIconModal] = useState(false)

  // Edicion rapida de nombre (doble click sobre el h2 del header)
  const [nameEditMode, setNameEditMode] = useState(false)
  const [nameEditValue, setNameEditValue] = useState('')
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false)

  // Split "Nombre Apellido(s)" -> { first_name, last_name }. Primer espacio separa.
  const splitFullName = (full) => {
    const trimmed = (full || '').trim()
    if (!trimmed) return { first_name: '', last_name: null }
    const idx = trimmed.indexOf(' ')
    if (idx === -1) return { first_name: trimmed, last_name: null }
    return { first_name: trimmed.slice(0, idx), last_name: trimmed.slice(idx + 1).trim() || null }
  }
  const beginNameEdit = () => {
    const initial = (prospect?.first_name ? `${prospect.first_name}${prospect.last_name ? ' ' + prospect.last_name : ''}` : '') || contact?.name || ''
    setNameEditValue(initial)
    setNameEditMode(true)
  }
  const applyNameEdit = (val) => {
    const { first_name, last_name } = splitFullName(val)
    // updateField ya tiene debounced save interno (useProspect)
    updateField('first_name', first_name)
    updateField('last_name', last_name)
  }
  const commitNameEdit = () => {
    applyNameEdit(nameEditValue)
    setNameEditMode(false)
  }
  const cancelNameEdit = () => {
    setNameEditMode(false)
    setNameEditValue('')
  }

  // Cargar iconos disponibles
  useEffect(() => {
    const loadAvailableIcons = async () => {
      try {
        const icons = await iconsAPI.getAvailableIcons()
        setAvailableIcons(icons)
      } catch (err) {
        console.error('Error cargando iconos disponibles:', err)
      }
    }
    loadAvailableIcons()
  }, [])

  // Cargar iconos del prospecto
  useEffect(() => {
    if (contact?.phone) {
      const loadProspectIcons = async () => {
        try {
          const icons = await iconsAPI.getProspectIcons(contact.phone)
          const current = icons.find(i => i.posicion === 1)
          setSelectedIcon(current ? current.id : null)
          setIconHistory(icons)
        } catch (err) {
          console.error('Error cargando iconos del prospecto:', err)
          setSelectedIcon(null)
          setIconHistory([])
        }
      }
      loadProspectIcons()
    }
  }, [contact?.phone])

  // Notes state (lifted from NotesSection)
  const [prospectNotes, setProspectNotes] = useState([])
  const [loadingNotes, setLoadingNotes] = useState(false)

  const loadNotes = useCallback(async () => {
    if (!contact?.phone) return
    try {
      setLoadingNotes(true)
      const data = await notesAPI.getNotes(contact.phone)
      setProspectNotes(data)
    } catch (err) {
      console.error('Error loading notes:', err)
    } finally {
      setLoadingNotes(false)
    }
  }, [contact?.phone])

  useEffect(() => { loadNotes() }, [loadNotes])

  const handleNoteAdded = async () => {
    await loadNotes()
    await refresh() // from useProspect hook - refreshes prospect data including next_followup_date
    if (onUpdate && contact?.phone) {
      onUpdate(contact.phone, { _forceRefresh: true })
    }
  }

  // Manejar seleccion de icono (single-select con historial)
  const handleIconSelect = async (iconId) => {
    if (iconId === selectedIcon) return

    setLoadingIcons(true)
    try {
      await iconsAPI.updateProspectIcon(contact.phone, iconId)
      setSelectedIcon(iconId)
      // Recargar historial completo
      const updatedIcons = await iconsAPI.getProspectIcons(contact.phone)
      setIconHistory(updatedIcons)
      // INC-036 (2026-05-04): pasar icon_current (name) en changes para que TodoListView
      // aplique optimistic update del icono en la grilla. Sin esto, el silentRefresh
      // posterior puede no traer al contacto en fresh (cae fuera del LIMIT por su
      // nueva prio IC) y la grilla queda con el icono viejo.
      const newIconName = availableIcons.find(i => i.id === iconId)?.name
      if (onUpdate) onUpdate(contact.phone, { _forceRefresh: true, icon_current: newIconName })
    } catch (err) {
      console.error('Error actualizando icono:', err)
    } finally {
      setLoadingIcons(false)
    }
  }

  const handleSaveNow = async () => {
    if (isChat) {
      const result = await saveNow()
      if (!result?.success) {
        console.error('Error al guardar:', result?.error)
      }
    } else {
      await saveNow()
    }
  }

  // Normalizar campo de agente (chat usa assignedTo, todolist usa assigned_to)
  // On-demand Wassenger assignment sync
  useEffect(() => {
    if (!contact?.phone) return
    let cancelled = false
    prospectAPI.syncAssignment(contact.phone).then(result => {
      if (!cancelled && result.changed && onUpdate) {
        onUpdate(contact.phone, { assigned_to: result.agent })
      }
    }).catch(() => {}) // silent fail
    return () => { cancelled = true }
  }, [contact?.phone])

  const currentAgent = contact?.assigned_to || contact?.assignedTo

  // Obtener urgencia de fecha
  const dateUrgency = getDateUrgency(prospect?.nextFollowupDate)

  // === Estados de carga y error ===

  if (!contact) {
    if (isChat) {
      return (
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-slate-100 dark:from-surface-dark dark:to-surface-dark-alt">
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-inner">
              <span className="text-4xl opacity-50">👤</span>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Selecciona un chat</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">para ver información del prospecto</p>
          </div>
        </div>
      )
    }
    return null
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-slate-700"></div>
            <div className="absolute inset-0 rounded-full border-4 border-primary-500 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Cargando prospecto...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <p className="text-sm text-red-600 font-medium mb-2">Error al cargar prospecto</p>
          <p className="text-xs text-gray-500">{error}</p>
        </div>
      </div>
    )
  }

  // timeAgo helper for contextual space
  const timeAgo = (dateStr) => {
    try { return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: es }) }
    catch { return dateStr }
  }

  const avatarName = contact.name || contact.phone

  // Elegir/limpiar avatar de rostro. Optimistic local + propaga a la grilla; revierte si falla.
  const handleAvatarSelect = async (seed) => {
    setAvatarPickerOpen(false)
    const prev = contact.avatar_seed
    if (seed === prev) return
    contact.avatar_seed = seed
    if (onUpdate) onUpdate(contact.phone, { avatar_seed: seed })
    try {
      await prospectAPI.setAvatar(contact.phone, seed)
    } catch (e) {
      console.error('setAvatar', e)
      contact.avatar_seed = prev
      if (onUpdate) onUpdate(contact.phone, { avatar_seed: prev })
    }
  }
  const prospectFullName = prospect?.first_name ? `${prospect.first_name}${prospect.last_name ? ' ' + prospect.last_name : ''}` : null
  const displayName = prospectFullName || contact.name || contact.phone

  return (
    <div className="flex-1 flex flex-col min-h-0 border-l-4 border-primary-500">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-br from-slate-50 via-white to-gray-50 dark:from-surface-dark-alt dark:via-surface-dark-alt dark:to-surface-dark">
        <div className="h-1 bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600" />

        <div className="p-5 pb-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <button type="button" onClick={() => setAvatarPickerOpen(true)} title="Cambiar avatar"
                className="block focus:outline-none">
                <ContactAvatar name={avatarName} phone={contact.phone} seed={contact.avatar_seed}
                  className="w-14 h-14 rounded-2xl shadow-lg text-2xl cursor-pointer hover:ring-2 hover:ring-primary-400 transition" />
              </button>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm" />
            </div>
            <div className="flex-1 min-w-0">
              {nameEditMode ? (
                <input
                  autoFocus
                  value={nameEditValue}
                  onChange={(e) => {
                    setNameEditValue(e.target.value)
                    applyNameEdit(e.target.value)
                  }}
                  onBlur={commitNameEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitNameEdit() }
                    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelNameEdit() }
                  }}
                  className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight bg-transparent border-b-2 border-primary-500 outline-none w-full"
                />
              ) : (
                <h2
                  onDoubleClick={beginNameEdit}
                  className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate leading-tight cursor-pointer hover:bg-gray-100/70 dark:hover:bg-slate-800/40 rounded px-1 -mx-1 transition-colors"
                  title="Doble click para editar nombre"
                >
                  {displayName}
                </h2>
              )}
              <div className="flex items-center gap-1.5">
                <p className="text-lg text-gray-500 dark:text-gray-400 font-medium">{formatPhoneCL(contact.phone)}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); const btn = e.currentTarget; navigator.clipboard.writeText(contact.phone).then(() => { btn.classList.add('text-green-500'); btn.querySelector('.icon-copy').style.display = 'none'; btn.querySelector('.icon-check').style.display = 'inline'; setTimeout(() => { try { btn.classList.remove('text-green-500'); btn.querySelector('.icon-copy').style.display = 'inline'; btn.querySelector('.icon-check').style.display = 'none' } catch(ex){} }, 1500) }) }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm cursor-pointer transition-colors duration-200"
                  title="Copiar telefono"
                ><svg xmlns="http://www.w3.org/2000/svg" className="icon-copy h-12 w-12 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg><svg xmlns="http://www.w3.org/2000/svg" className="icon-check h-12 w-12 inline" style={{display:'none'}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></button>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* CallToggle hidden - not in use */}
              <button
                onClick={handleSaveNow}
                disabled={saving}
                className="py-3 px-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 hover:scale-105 hover:shadow-lg hover:shadow-primary-500/40 transition-all disabled:opacity-50 shadow-md"
                title="Guardar cambios"
              >
                {saving ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                    <polyline points="17 21 17 13 7 13 7 21"/>
                    <polyline points="7 3 7 8 15 8"/>
                  </svg>
                )}
              </button>
              <button
                onClick={onClose}
                className="py-3 px-2.5 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-slate-700 dark:to-slate-800 text-gray-500 dark:text-gray-400 hover:from-primary-500 hover:to-primary-600 dark:hover:from-primary-500 dark:hover:to-primary-600 hover:text-white dark:hover:text-white hover:scale-110 hover:shadow-lg hover:shadow-primary-400/50 hover:ring-2 hover:ring-primary-300/60 transition-all duration-200 group animate-panel-pulse hover:animate-none"
                title="Cerrar panel"
              >
                <svg className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Data tags */}
          {(prospect?.email || prospect?.business_type || prospect?.cuando_implementar || prospect?.location || prospect?.product_interest || prospect?.vendido || prospect?.no_contesta_dia != null) && (
            <div className="flex items-center gap-1.5 flex-wrap mt-1" style={{ marginLeft: '66px' }}>
              {prospect?.email && <span className="text-sm px-2 py-0.5 rounded-full border bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700">{prospect.email}</span>}
              {prospect?.business_type && <span className="text-sm px-2 py-0.5 rounded-full border bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700">{prospect.business_type}</span>}
              {prospect?.cuando_implementar && <span className="text-sm px-2 py-0.5 rounded-full border bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-700">{prospect.cuando_implementar}</span>}
              {prospect?.location && <span className="text-sm px-2 py-0.5 rounded-full border bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700">{prospect.location}</span>}
              {prospect?.product_interest && <span className="text-sm px-2 py-0.5 rounded-full border bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700">{prospect.product_interest}</span>}
              {prospect?.vendido && <span className="text-sm px-2 py-0.5 rounded-full font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">{'✓'} Vendido</span>}
              {prospect?.no_contesta_dia != null && <span className="text-xs px-2 py-0.5 rounded-full border font-medium bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700">(DIA {prospect.no_contesta_dia})</span>}
            </div>
          )}
          {prospect && (
            <div className="mt-2" style={{ marginLeft: '66px' }}>
              <button
                onClick={() => updateField('no_contactar', !prospect.no_contactar)}
                className={`text-xs px-2.5 py-1 rounded-full border font-medium transition ${prospect.no_contactar ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700' : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-red-300 hover:text-red-600 dark:bg-slate-800 dark:text-gray-400 dark:border-slate-700'}`}
                title="No contactar: bloquea inscribir a este prospecto en cualquier campaña (todas las categorías)"
              >
                {prospect.no_contactar ? '🚫 No contactar' : 'No contactar'}
              </button>
            </div>
          )}

          {/* Agente asignado + Calificacion */}
          <div className="mt-2 mb-1 flex items-center gap-2">
            {(() => {
              const currentIconRaw = iconHistory.find(ic => ic.posicion === 1)?.nombre || null
              const currentIconKey = currentIconRaw ? (NOMBRE_MAP[currentIconRaw] || currentIconRaw) : null
              const desc = currentIconKey
                ? (ICON_DESCRIPTIONS[currentIconKey] || { title: currentIconKey, body: '' })
                : { title: 'Sin calificación', body: 'Click para asignar una calificación al prospecto.' }
              return (
                <Tooltip title={desc.title} body={desc.body} delay={300}>
                  <button
                    onClick={() => setShowIconModal(true)}
                    className="w-9 h-9 rounded-lg border-2 border-gray-200 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all flex items-center justify-center bg-white dark:bg-slate-800 flex-shrink-0"
                    aria-label={desc.title}
                  >
                    <RatingIcon iconId={currentIconRaw} size="md" />
                  </button>
                </Tooltip>
              )
            })()}
            <AgentSelector
              phone={contact.phone}
              currentAgent={currentAgent}
              onAssign={(agentName, agentId, warning) => {
                if (onUpdate) onUpdate(contact.phone, { assigned_to: agentName })
                if (warning) {
                  const toast = document.createElement('div')
                  toast.className = 'fixed bottom-4 right-4 z-[100] bg-amber-50 border border-amber-300 text-amber-800 text-sm px-4 py-3 rounded-xl shadow-lg max-w-sm'
                  toast.textContent = warning
                  document.body.appendChild(toast)
                  setTimeout(() => toast.remove(), 5000)
                }
              }}
            />
            {contact?.watching_agents && contact.watching_agents.length > 0 && (
              <div
                className="ml-auto inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 max-w-full overflow-hidden"
                title={`${contact.watching_agents.join(', ')} ${contact.watching_agents.length === 1 ? 'esta' : 'estan'} mirando este contacto`}
              >
                {contact.watching_agents.slice(0, 3).map(name => (
                  <span key={name} className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: colorForAgent(name) }} />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[80px]">{name}</span>
                  </span>
                ))}
                {contact.watching_agents.length > 3 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">+{contact.watching_agents.length - 3}</span>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto scroll-smooth bg-gray-100 dark:bg-surface-dark">
        {/* Icon Modal (Calificacion) */}
        {showIconModal && (
          <IconSelectorModal
            phone={contact.phone}
            currentIconId={selectedIcon}
            onClose={() => setShowIconModal(false)}
            onIconChanged={async () => {
              const updatedIcons = await iconsAPI.getProspectIcons(contact.phone)
              const current = updatedIcons.find(ic => ic.posicion === 1)
              setSelectedIcon(current ? current.id : null)
              setIconHistory(updatedIcons)
              setShowIconModal(false)
              // INC-036: pasar icon_current (name) para optimistic update en grilla
              const newIconName = current ? availableIcons.find(i => i.id === current.id)?.name : null
              if (onUpdate) onUpdate(contact.phone, { _forceRefresh: true, icon_current: newIconName })
            }}
          />
        )}

        {/* Datos Clave */}
        <AccordionSection
          title="Datos Clave"
          sectionBg="bg-blue-100 dark:bg-blue-900/30"
          icon={'📋'}
          defaultOpen={false}
          badge={[prospect?.first_name, prospect?.last_name, contact?.phone, prospect?.email, prospect?.hint, prospect?.product_interest, prospect?.business_type, prospect?.cuando_implementar].filter(v => v && String(v).trim()).length + '/8'}
          badgeVariant={[prospect?.first_name, prospect?.last_name, contact?.phone, prospect?.email, prospect?.hint, prospect?.product_interest, prospect?.business_type, prospect?.cuando_implementar].filter(v => v && String(v).trim()).length === 8 ? 'success' : 'warning'}
        >
          <FormField
            value={prospect?.first_name || ''}
            onChange={(v) => updateField('first_name', v)}
            placeholder="Nombre del prospecto"
          />
          <FormField
            value={prospect?.last_name || ''}
            onChange={(v) => updateField('last_name', v)}
            placeholder="Apellido del prospecto"
          />
          <FormField
            value={contact?.phone || ''}
            onChange={() => {}}
            placeholder="+56912345678"
            disabled
          />
          <FormField
            type="email"
            value={prospect?.email || ''}
            onChange={(v) => updateField('email', v)}
            placeholder="correo@ejemplo.com"
          />
          <FormField
            value={prospect?.hint || ''}
            onChange={(v) => updateField('hint', v)}
            placeholder="Nota Express"
          />
          <FormField
            value={prospect?.product_interest || ''}
            onChange={(v) => updateField('product_interest', v)}
            placeholder="Producto de interes"
          />
          <FormField
            value={prospect?.business_type || ''}
            onChange={(v) => updateField('business_type', v)}
            placeholder="Tipo de negocio"
          />
          <FormField
            value={prospect?.cuando_implementar || ''}
            onChange={(v) => updateField('cuando_implementar', v)}
            placeholder="Cuando desea implementar"
          />
        </AccordionSection>

        {/* Notas */}
        <NotesSection
          key={contact.phone}
          initialMessage={prospect?.initial_message}
          productInterest={prospect?.product_interest}
          noContestaDay={prospect?.no_contesta_dia}
          nextFollowupDate={prospect?.nextFollowupDate}
          phone={contact.phone}
          notes={prospectNotes}
          onNoteAdded={handleNoteAdded}
          onDraftChanged={onDraftChanged}
          {...(!isChat && { onProspectUpdate: handleNotesUpdate })}
          contactName={contact?.name}
          currentIcon={contact?.icon_current}
          onIcPendingEnqueue={onIcPendingEnqueue}
        />

        {/* Análisis IA Supervisor */}
        <SupervisorAnalysis phone={contact.phone} />

        {/* Campañas */}
        <AccordionSection title="Campañas" defaultOpen={false}>
          <ProspectCampaignsSection contactPhone={contact && contact.phone} />
        </AccordionSection>

        {/* Tickets de descuento (Ticket Club — escalera de precios, E3). key por phone
            (patrón NotesSection): al cambiar de prospecto se REMONTA — jamás mostrar la
            escalera (¡ni los códigos!) del prospecto anterior (hallazgo del revisor). */}
        <AccordionSection title="Tickets de descuento" icon="🎟️" defaultOpen={false}>
          <TicketsSection key={contact && contact.phone} contactPhone={contact && contact.phone} />
        </AccordionSection>

        {/* Historial de Llamadas - hidden */}

        {/* Fecha de Vencimiento - hidden */}

        {/* Informacion Comercial - hidden */}

        {/* Negocio */}
        <AccordionSection title="Negocio"
          sectionBg="bg-amber-100 dark:bg-amber-900/30" icon="🏢">
          <FormField
            label="Tipo de Negocio"
            value={prospect?.business_type || ''}
            onChange={(v) => updateField('business_type', v)}
            placeholder="Ej: Restaurante, Almacén, Minimarket"
          />
          <FormField
            label="Comuna"
            value={prospect?.location || ''}
            onChange={(v) => updateField('location', v)}
            placeholder="Ej: Santiago, Providencia, etc."
          />
          <FormField
            label="Modelo de Funcionamiento"
            value={prospect?.business_model || ''}
            onChange={(v) => updateField('business_model', v)}
            placeholder="Ej: Delivery, Local físico, Ambos"
            multiline
          />
        </AccordionSection>

        {/* Timing & Presupuesto */}
        <AccordionSection title="Timing & Presupuesto"
          sectionBg="bg-rose-100 dark:bg-rose-900/30" icon="⏰">
          <FormField
            label="Plazo de Decisión"
            value={prospect?.decision_timeline || ''}
            onChange={(v) => updateField('decision_timeline', v)}
            placeholder="Ej: Esta semana, Mes próximo, etc."
          />
          <FormField
            label="Presupuesto Estimado"
            value={prospect?.budget || ''}
            onChange={(v) => updateField('budget', v)}
            placeholder="Ej: $500.000 - $1.000.000"
          />
          {/* Chat-only: horarios preferidos */}
          {isChat && (
            <>
              <FormField
                label="Horario Preferido 1"
                value={prospect?.preferred_contact_time_1 || ''}
                onChange={(v) => updateField('preferred_contact_time_1', v)}
                placeholder="Ej: Mañanas 9-12"
              />
              <FormField
                label="Horario Preferido 2"
                value={prospect?.preferred_contact_time_2 || ''}
                onChange={(v) => updateField('preferred_contact_time_2', v)}
                placeholder="Ej: Tardes 15-18"
              />
            </>
          )}
        </AccordionSection>

        {/* TodoList-only: Estado de Venta */}
        {!isChat && (
          <div className={`p-4 border-b-2 border-gray-300 dark:border-surface-dark-border transition-all duration-300 ${
            prospect?.vendido
              ? 'bg-gradient-to-r from-green-100 via-emerald-50 to-teal-50 dark:from-green-900/30 dark:via-emerald-900/20 dark:to-teal-900/20'
              : 'bg-gradient-to-r from-cyan-50 to-sky-50 dark:from-cyan-900/20 dark:to-sky-900/20'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">{prospect?.vendido ? '🎉' : '💰'}</span>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-200">Estado de Venta</h3>
              </div>
              <button
                onClick={() => {
                  const newVendido = !prospect?.vendido
                  updateField('vendido', newVendido)
                  if (newVendido) {
                    updateField('vendidoAt', new Date().toISOString())
                  }
                }}
                className={`
                  relative w-14 h-7 rounded-full transition-all duration-300
                  ${prospect?.vendido
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg shadow-green-200 dark:shadow-green-900/30'
                    : 'bg-gray-300 hover:bg-gray-400 dark:bg-slate-600 dark:hover:bg-slate-500'
                  }
                `}
              >
                <span className={`
                  absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300
                  ${prospect?.vendido ? 'left-8' : 'left-1'}
                `} />
              </button>
            </div>

            {prospect?.vendido && (
              <div className="space-y-3 mt-4 pt-3 border-t border-green-200 dark:border-green-800/50">
                <div className="flex items-center gap-2 text-sm text-green-700 mb-3">
                  <span className="animate-pulse">✅</span>
                  <span className="font-medium">Marcado como vendido</span>
                  {prospect?.vendidoAt && (
                    <span className="text-green-600 text-xs">
                      ({new Date(prospect.vendidoAt).toLocaleDateString('es-CL')})
                    </span>
                  )}
                </div>
                <FormField
                  label="Monto de Venta"
                  value={prospect?.ventaMonto || ''}
                  onChange={(v) => updateField('ventaMonto', v)}
                  placeholder="Ej: 450000"
                  type="number"
                />
                <FormField
                  label="Notas de Venta"
                  value={prospect?.ventaNotas || ''}
                  onChange={(v) => updateField('ventaNotas', v)}
                  placeholder="Detalles de la venta, producto, etc."
                  multiline
                />
              </div>
            )}

            {!prospect?.vendido && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Activa este toggle cuando el contacto haya realizado una compra.
                Los contactos vendidos se excluyen de campañas automáticas.
              </p>
            )}
          </div>
        )}

        {/* Necesidades Críticas */}
        <AccordionSection title="Necesidades Críticas"
          sectionBg="bg-red-100 dark:bg-red-900/30" icon="⚠️">
          <FormField
            label="Problema 1"
            value={prospect?.pain_point_1 || ''}
            onChange={(v) => updateField('pain_point_1', v)}
            placeholder="Principal problema a resolver"
            multiline
          />
          <FormField
            label="Problema 2"
            value={prospect?.pain_point_2 || ''}
            onChange={(v) => updateField('pain_point_2', v)}
            placeholder="Segundo problema a resolver"
            multiline
          />

          {/* Chat-only: Botones Rojos */}
          {isChat && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Botones Rojos (Críticos para cierre)
              </p>
              <FormField
                label="Botón Rojo 1"
                value={prospect?.red_button_1 || ''}
                onChange={(v) => updateField('red_button_1', v)}
                placeholder="Acción crítica #1"
              />
              <FormField
                label="Botón Rojo 2"
                value={prospect?.red_button_2 || ''}
                onChange={(v) => updateField('red_button_2', v)}
                placeholder="Acción crítica #2"
              />
              <FormField
                label="Botón Rojo 3"
                value={prospect?.red_button_3 || ''}
                onChange={(v) => updateField('red_button_3', v)}
                placeholder="Acción crítica #3"
              />
            </div>
          )}
        </AccordionSection>

        {/* Chat-only: Información Técnica */}
        {isChat && (
          <AccordionSection title="Información Técnica"
          sectionBg="bg-violet-100 dark:bg-violet-900/30" icon="📱">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-400 mb-2">Dispositivo</label>
                {contact?.deviceSource ? (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200">
                    {contact.deviceSource === 'mobile' && <>📱 <span>Mobile</span></>}
                    {contact.deviceSource === 'web' && <>💻 <span>Web</span></>}
                    {contact.deviceSource === 'business_api' && <>🔧 <span>Business API</span></>}
                    {contact.deviceSource === 'api' && <>🔧 <span>API</span></>}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-slate-600">
                    ❓ <span>No disponible</span>
                  </div>
                )}
              </div>

              {contact?.deviceInfoUpdatedAt && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-400 mb-1">Última actualización</label>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {new Date(contact.deviceInfoUpdatedAt).toLocaleString('es-CL', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}

              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-100 dark:border-blue-800/40 rounded-xl">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>💡 Información pasiva:</strong> El dispositivo se captura automáticamente cuando el contacto envía un mensaje.
                </p>
              </div>
            </div>
          </AccordionSection>
        )}
      </div>

      <AvatarPickerModal open={avatarPickerOpen} currentSeed={contact.avatar_seed}
        onClose={() => setAvatarPickerOpen(false)} onSelect={handleAvatarSelect} />
    </div>
  )
}
